import React, { useMemo } from "react";
import Lottie from "lottie-react";
import Animate from "../../assets/loader.json";
import { useTranslation } from "react-i18next";

const rankClassName = (index) => {
  if (index === 0) return "ai-rank-gold";
  if (index === 1) return "ai-rank-silver";
  if (index === 2) return "ai-rank-bronze";
  return "";
};

// [parsedPayload.horses key, header label]. Older analyses (v1) keep their original columns.
const V1_SCORE_COLUMNS = [
  ["form", "Form"],
  ["classScore", "Class"],
  ["dist", "Dist"],
  ["trainer", "Trainer"],
  ["jockey", "Jockey"],
  ["combo", "Combo"],
  ["wet", "Wet"],
  ["draw", "Draw"],
  ["wt", "Wgt"],
  ["pedigree", "Pedigree"],
  ["train", "Train"],
  ["bigRace", "Big Race"],
  ["days", "Days"],
];

const V2_SCORE_COLUMNS = [
  ["form", "Form"],
  ["beaten", "Beaten"],
  ["classFit", "Class"],
  ["dist", "Dist"],
  ["course", "Course"],
  ["draw", "Draw"],
  ["wt", "Wgt"],
  ["trainer", "Trainer"],
  ["jockey", "Jockey"],
  ["combo", "Combo"],
  ["pedigree", "Pedigree"],
];

const formatNumber =(value, withPercent = false) => {
  // v2 sends null for factors the model could not score (NA); Number(null) would show 0.00.
  if (value === null || value === undefined || value === "") return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return withPercent ? `${n.toFixed(2)}%` : n.toFixed(2);
};

const AiAnalysis = ({ raceAiAnalysis, aiStatus, aiErrorMessage }) => {
  const { t, i18n } = useTranslation();
  const cookiedata = i18n.language;
  const horses = useMemo(() => {
    const rows = raceAiAnalysis?.data?.parsedPayload?.horses;
    if (!Array.isArray(rows)) return [];
    // The final flow's analyst sets the order; older analyses are ordered by their chance.
    if (raceAiAnalysis?.data?.parsedPayload?.flow === "v3") {
      return [...rows].sort((a, b) => (Number(a?.rank) || 999) - (Number(b?.rank) || 999));
    }
    return [...rows].sort(
      (a, b) => (Number(b?.winPct) || 0) - (Number(a?.winPct) || 0)
    );
  }, [raceAiAnalysis]);

  const raceConfidence = raceAiAnalysis?.data?.parsedPayload?.raceConfidence;

  const scoreColumns =
    raceAiAnalysis?.data?.parsedPayload?.schema === "v2" ? V2_SCORE_COLUMNS : V1_SCORE_COLUMNS;
  const gridStyle = {
    gridTemplateColumns: `0.6fr 2fr repeat(${scoreColumns.length + 2}, 0.9fr)`,
  };

  if (aiStatus === "loading") {
    return <Lottie animationData={Animate} className="Lottie compLottie" />;
  }

  if (aiStatus === "error") {
    return (
      <p className="ai-analysis-empty">
        {aiErrorMessage || t("Unable to load AI analysis.")}
      </p>
    );
  }

  if (!horses.length) {
    return <p className="ai-analysis-empty">{t("No AI analysis is available for this race.")}</p>;
  }

  return (
    <div className="track-new-master-wrapper ai-analysis-wrapper">
      {raceConfidence && (
        <p className="ai-analysis-note">
          {t("AI confidence")}: {t(`AI confidence ${raceConfidence}`)}
        </p>
      )}
      <div className="track-new-scroll-container">
        <div className="ai-grid-thead" style={gridStyle}>
          <div>{t("Rank")}</div>
          <div>{t("Horse")}</div>
          <div>{t("Wins%")}</div>
          <div>{t("Overall")}</div>
          {scoreColumns.map(([key, label]) => (
            <div key={key}>{t(label)}</div>
          ))}
        </div>
        <div className="ai-grid-tbody">
          {horses.map((horse, index) => (
            <div
              key={`${horse?.horseName || "horse"}-${index}`}
              className={`ai-grid-row-card ${rankClassName(index)}`}
              style={gridStyle}
            >
              <div>{index + 1}</div>
              <div>
                {cookiedata === "ar" ? (horse?.horseNameAr || horse?.horseName) : (horse?.horseName || "-")}
                {horse?.horseNo != null && horse?.horseNo !== "" ? ` (${horse.horseNo})` : ""}
              </div>
              <div>{formatNumber(horse?.winPct, true)}</div>
              <div>{formatNumber(horse?.overallScore)}</div>
              {scoreColumns.map(([key]) => (
                <div key={key}>{formatNumber(horse?.[key])}</div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <p className="ai-analysis-note">{t("Top 3 horses are highlighted.")}</p>
    </div>
  );
};

export default AiAnalysis;
